// Admin "クーポンカテゴリー" page (/admin/coupons)
//
// Lists every coupon in the system joined with its hub and lets an admin set
// the category column on each row. Used post-migration to backfill the
// uncategorized production coupons.

import { FC, useCallback, useEffect, useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { http } from "../../http";
import { API_BASE_URL } from "../../Constants";
import ErrorBanner from "Components/ErrorBanner";

interface AdminCoupon {
  id: number;
  hub_id: number;
  title: string;
  category: string;
  hub_name: string;
  hub_state: string;
  hub_city: string;
  hub_sponsor_id: number;
}

interface CouponsResponse {
  coupons: AdminCoupon[];
}

// Mirror of server/app/services/coupon.go normalizeCategory + front redesign api.ts
const CATEGORIES: { value: string; label: string }[] = [
  { value: "cafe", label: "カフェ" },
  { value: "meal", label: "ごはん" },
  { value: "sweet", label: "スイーツ" },
  { value: "bar", label: "バー" },
  { value: "shop", label: "ショップ" },
  { value: "salon", label: "サロン" },
  { value: "activity", label: "体験" },
];

export const CouponsPage: FC = () => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await http<CouponsResponse>(
        `${API_BASE_URL}/admin/coupons`,
        "GET"
      );
      if (!res.parsedBody) throw new Error("response error");
      setCoupons(res.parsedBody.coupons || []);
    } catch (ex: any) {
      setError(typeof ex === "string" ? ex : ex?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = async (id: number, category: string) => {
    setSavingId(id);
    // Optimistic update — revert on failure.
    const prev = coupons;
    setCoupons((cs) => cs.map((c) => (c.id === id ? { ...c, category } : c)));
    try {
      await http(`${API_BASE_URL}/admin/coupons/${id}/category`, "POST", {
        category,
      });
    } catch (ex: any) {
      setCoupons(prev);
      setError(
        typeof ex === "string" ? ex : ex?.message || "カテゴリーを保存できませんでした"
      );
    } finally {
      setSavingId(null);
    }
  };

  const uncategorizedCount = coupons.filter((c) => c.category === "shop").length;

  return (
    <Container component="main" maxWidth="xl" style={{ marginTop: 32 }}>
      <Typography variant="h5" component="h1" style={{ marginBottom: 8 }}>
        クーポンカテゴリー管理
      </Typography>
      <Typography
        variant="body2"
        style={{ marginBottom: 16, color: "rgba(0,0,0,0.6)" }}
      >
        新しいリデザインの discover / map 画面では、各クーポンの「カテゴリー」によって表示するアイコンや絞り込みが決まります。
        マイグレーション直後はデフォルトの「ショップ」になっているので、正しい区分に変更してください。
      </Typography>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <Chip
          label={`合計 ${coupons.length} 件`}
          variant="outlined"
          size="small"
        />
        {uncategorizedCount > 0 && (
          <Chip
            label={`要確認 (shop): ${uncategorizedCount} 件`}
            color="warning"
            size="small"
          />
        )}
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}
        >
          <CircularProgress />
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>店舗</TableCell>
                <TableCell>エリア</TableCell>
                <TableCell>クーポン名</TableCell>
                <TableCell style={{ width: 200 }}>カテゴリー</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.hub_name}</TableCell>
                  <TableCell style={{ color: "rgba(0,0,0,0.6)" }}>
                    {c.hub_state}
                    {c.hub_city ? `・${c.hub_city}` : ""}
                  </TableCell>
                  <TableCell>{c.title}</TableCell>
                  <TableCell>
                    <Select
                      value={c.category || "shop"}
                      onChange={(e) => onChange(c.id, e.target.value as string)}
                      size="small"
                      disabled={savingId === c.id}
                      fullWidth
                    >
                      {CATEGORIES.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};
